import { useEffect, useRef, useState } from 'react';
import { cn } from '~/lib/utils';
import styles from './SignInUI.module.css';

/**
 * Cloudflare Turnstile, the check that a person and not a script is asking for a magic link. Supabase
 * verifies the token it produces, so a bot calling Supabase's public sign-in API directly is stopped
 * too, not only one that uses this page.
 *
 * It stays out of sight, and takes no room, unless Cloudflare needs the person to click something
 * ("interaction-only").
 * A token pays for one send, so `resetSignal` asks for a fresh one after every answer from the server.
 */

interface TurnstileApi {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptLoading: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptLoading) {
    scriptLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile missing')));
      script.onerror = () => {
        scriptLoading = null;
        reject(new Error('Turnstile failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptLoading;
}

export interface TurnstileWidgetProps {
  siteKey: string;
  /** Any value that changes when the last token was spent. */
  resetSignal: unknown;
  /** The current token, or null while there is none (loading, expired, spent). */
  onToken: (token: string | null) => void;
  /**
   * The check could not load, or Cloudflare would not pass this browser (a VPN, a privacy browser, an
   * embedded or automated one). Cloudflare retries on its own; a token arriving later clears it.
   */
  onTrouble: () => void;
}

export const TurnstileWidget = ({ siteKey, resetSignal, onToken, onTrouble }: TurnstileWidgetProps) => {
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const callbacks = useRef({ onToken, onTrouble });
  callbacks.current = { onToken, onTrouble };
  const [shown, setShown] = useState(false);

  // Cloudflare decides when the check needs a click; its height is the only sign of that.
  useEffect(() => {
    const box = container.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setShown(box.offsetHeight > 0));
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) return;
        widgetId.current = turnstile.render(container.current, {
          sitekey: siteKey,
          theme: 'light',
          size: 'flexible',
          appearance: 'interaction-only',
          'response-field': false,
          callback: (token: string) => callbacks.current.onToken(token),
          'expired-callback': () => callbacks.current.onToken(null),
          'timeout-callback': () => callbacks.current.onToken(null),
          'error-callback': () => {
            callbacks.current.onToken(null);
            callbacks.current.onTrouble();
          },
        });
      })
      .catch(() => {
        if (!cancelled) callbacks.current.onTrouble();
      });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey]);

  useEffect(() => {
    if (!widgetId.current || !window.turnstile) return;
    callbacks.current.onToken(null);
    window.turnstile.reset(widgetId.current);
  }, [resetSignal]);

  return <div ref={container} className={cn(styles.captcha, !shown && styles.captchaHidden)} />;
};
